import { useState } from 'react';
import { X, Upload, Camera, User, AlertTriangle } from 'lucide-react';
import type { UserProfile } from '@/types';
import { VersionInfo } from './VersionInfo';

interface ProfileSettingsProps {
  profile: UserProfile | null;
  onClose: () => void;
  onUpdateProfile: (updates: { displayName?: string; shareStats?: boolean; shareFlights?: boolean; profilePicture?: string }) => Promise<void>;
  onDeleteAccount?: () => Promise<void>;
}

export function ProfileSettings({ profile, onClose, onUpdateProfile, onDeleteAccount }: ProfileSettingsProps) {
  const [displayName, setDisplayName] = useState(profile?.displayName || '');
  const [shareStats, setShareStats] = useState(profile?.shareStats ?? true);
  const [shareFlights, setShareFlights] = useState(profile?.shareFlights ?? false);
  const [profilePicture, setProfilePicture] = useState(profile?.profilePicture || '');
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleUpdateProfile = async () => {
    try {
      await onUpdateProfile({ displayName, shareStats, shareFlights, profilePicture });
      onClose();
    } catch (error) {
      console.error('Failed to update profile:', error);
      // Error handling is left to the parent component
    }
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be smaller than 2MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setUploadingPicture(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setProfilePicture(base64String);
        setUploadingPicture(false);
      };
      reader.onerror = () => {
        alert('Failed to read image');
        setUploadingPicture(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload picture:', error);
      alert('Failed to upload picture');
      setUploadingPicture(false);
    }
  };

  const handleRemoveProfilePicture = () => {
    setProfilePicture('');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      return;
    }

    if (!onDeleteAccount) {
      setDeleteError('Delete account functionality is not available');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDeleteAccount();
      // The parent component will handle sign out and redirect
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="glass rounded-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6 text-primary-500" />
            <h2 className="text-2xl font-bold">Profile Settings</h2>
          </div>
          <button onClick={onClose} className="btn-ghost p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto flight-scroll p-6">
          <div className="space-y-6">
            {/* Profile Picture */}
            <div>
              <label className="block text-sm font-medium mb-3">Profile Picture</label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-white/5 border-2 border-white/10 flex items-center justify-center overflow-hidden">
                    {profilePicture ? (
                      <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <Camera className="w-8 h-8 text-white/40" />
                    )}
                  </div>
                  {uploadingPicture && (
                    <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <label className="btn-secondary cursor-pointer inline-flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    Upload Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="hidden"
                      disabled={uploadingPicture}
                    />
                  </label>
                  {profilePicture && (
                    <button
                      onClick={handleRemoveProfilePicture}
                      className="btn-ghost text-red-400 hover:bg-red-500/10 text-sm"
                      disabled={uploadingPicture}
                    >
                      Remove Photo
                    </button>
                  )}
                  <p className="text-xs text-white/40">Max 2MB, JPG/PNG</p>
                </div>
              </div>
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium mb-2">Display Name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 focus:outline-none focus:border-primary-500/50"
                placeholder="Your display name"
              />
            </div>

            {/* Privacy Settings */}
            <div className="space-y-3">
              <label className="block text-sm font-medium mb-2">Privacy Settings</label>
              
              <label className="flex items-center gap-3 glass-card p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareStats}
                  onChange={(e) => setShareStats(e.target.checked)}
                  className="w-5 h-5 rounded border-white/20 text-primary-500 focus:ring-primary-500"
                />
                <div>
                  <p className="font-medium">Share Statistics</p>
                  <p className="text-sm text-white/60">Allow friends to see your flight stats</p>
                </div>
              </label>

              <label className="flex items-center gap-3 glass-card p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={shareFlights}
                  onChange={(e) => setShareFlights(e.target.checked)}
                  className="w-5 h-5 rounded border-white/20 text-primary-500 focus:ring-primary-500"
                />
                <div>
                  <p className="font-medium">Share Flight Details</p>
                  <p className="text-sm text-white/60">Allow friends to see your detailed flight list</p>
                </div>
              </label>
            </div>

            {/* Save Button */}
            <button
              onClick={handleUpdateProfile}
              className="btn-primary w-full"
            >
              Save Settings
            </button>

            {/* Danger Zone - Delete Account */}
            <div className="border-t border-red-500/20 pt-6 mt-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="text-lg font-semibold text-red-400">Danger Zone</h3>
              </div>
              <p className="text-sm text-white/60 mb-4">
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteConfirmation(true)}
                className="btn-ghost text-red-400 hover:bg-red-500/10 w-full border border-red-500/20"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmation && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-slate-900 backdrop-blur-sm rounded-2xl">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-md mx-4">
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                <h3 className="text-xl font-bold text-red-400">Confirm Account Deletion</h3>
              </div>
              
              <p className="text-white/80 mb-4">
                This will permanently delete:
              </p>
              <ul className="list-disc list-inside text-white/60 mb-4 space-y-1">
                <li>All your flight data</li>
                <li>All friendships and friend requests</li>
                <li>Your profile and settings</li>
                <li>Your account credentials</li>
              </ul>
              
              <p className="text-white/80 mb-4 font-medium">
                This action cannot be undone!
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Type <span className="font-mono bg-red-500/20 px-2 py-1 rounded">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full bg-white/5 border border-red-500/30 rounded-lg px-4 py-2 focus:outline-none focus:border-red-500"
                  placeholder="Type DELETE"
                  disabled={isDeleting}
                />
              </div>
              
              {deleteError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {deleteError}
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirmation(false);
                    setDeleteConfirmText('');
                    setDeleteError(null);
                  }}
                  className="btn-secondary flex-1"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Footer with version info */}
        <div className="mt-6 pt-4 border-t border-white/10 text-center">
          <VersionInfo showDetails />
        </div>
      </div>
    </div>
  );
}
